import { useRef, useState } from "react";
import clsx from "clsx";
import { DateTime } from "luxon";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";
import { api, ApiError, type ImportLog } from "../lib/api";

/**
 * One button that takes an airline's load spreadsheet and puts its passenger numbers on
 * the board. It lives on the pages where people go looking for those numbers, not only
 * under Settings, because the upload nobody can find might as well not exist.
 */

const btn = "flex items-center gap-1 rounded-lg border border-line px-3 py-1.5 text-[12.5px] font-semibold hover:border-ink-3 disabled:opacity-50";

/** "30 Sep-15 Oct", or one day when the file covers one; the year only when the two differ. */
export function dateSpan(dates: ImportLog["dates"]): string | null {
  if (!dates) return null;
  const from = DateTime.fromISO(dates.from);
  const to = DateTime.fromISO(dates.to);
  if (dates.from === dates.to) return from.toFormat("d LLL");
  if (from.year !== to.year) return `${from.toFormat("d LLL yyyy")}-${to.toFormat("d LLL yyyy")}`;
  return `${from.toFormat("d LLL")}-${to.toFormat("d LLL")}`;
}

/**
 * What the file turned out to hold, in one line. The count alone does not say whether
 * the right fortnight or the right airline just went in; the dates and carriers do.
 */
export function uploadResultLine(name: string, r: ImportLog): string {
  if (r.records === 0) return `${name}: nothing read. ${r.note}`;
  const span = dateSpan(r.dates);
  const who = r.carriers.length ? ` (${r.carriers.join(", ")})` : "";
  const loads = `${r.records} ${r.records === 1 ? "load" : "loads"}${span ? ` for ${span}` : ""}${who}`;
  return `${name}: ${loads}, ${r.flightsOnBoard} matched to flights on the board.`;
}

export function UploadLoads({ compact = false, onDone }: { compact?: boolean; onDone?: () => void }) {
  const qc = useQueryClient();
  const file = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ line: string; note: string | null; bad: boolean } | null>(null);

  const upload = async (f: File) => {
    setBusy(true);
    setResult(null);
    try {
      const buf = await f.arrayBuffer();
      let bin = "";
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      const r = await api.uploadLoads(f.name, btoa(bin));
      setResult({ line: uploadResultLine(f.name, r), note: r.records ? r.note : null, bad: r.records === 0 });
      await qc.invalidateQueries();
      onDone?.();
    } catch (err) {
      setResult({ line: err instanceof ApiError ? err.message : "Upload failed", note: null, bad: true });
    } finally {
      setBusy(false);
      if (file.current) file.current.value = "";
    }
  };

  return (
    <div className={clsx(compact ? "flex flex-wrap items-center gap-2" : "")}>
      <input ref={file} type="file" accept=".xlsx,.xlsm,.csv,.txt" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      <button className={btn} disabled={busy} onClick={() => file.current?.click()} title="An Excel or CSV file with flight numbers and passengers and/or bags">
        {busy ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />} Upload loads (.xlsx / .csv)
      </button>
      {result && (
        <div className={clsx("text-[12px]", compact ? "basis-full" : "mt-2", result.bad ? "text-warn" : "text-ink-2")}>
          {result.line}
          {result.note && <span className="text-ink-3"> {result.note}</span>}
        </div>
      )}
    </div>
  );
}
